import { useApolloClient } from '@apollo/client';
import { Alert, Button, Card, Form, message } from 'antd';
import { Formik } from 'formik';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { type RouterSpecsFragment } from '../../../api/fragments/RouterSpecs';
import useLoadingButton from '../../../components/button/useLoadingButton';
import useHandleError from '../../../utilities/useHandleError';
import { type EndpointClass } from './getAvailableEndpoints';

export type EndpointClassConfigurationProps = {
    router: RouterSpecsFragment;
    endpointClass: EndpointClass;
    goBackToSelection: () => void;
};

const EndpointClassConfiguration = ({ router, endpointClass, goBackToSelection }: EndpointClassConfigurationProps) => {
    const { t } = useTranslation(['routerDetails']);
    const { setLoading, LoadingButton } = useLoadingButton();
    const routerId = router.id;

    const { submit } = endpointClass;

    const apolloClient = useApolloClient();
    const navigate = useNavigate();
    const onSubmit = useHandleError(
        async values => {
            setLoading(true);
            message.loading({
                content: t('routerDetails:addEndpoint.configuration.messages.submitting'),
                key: 'primary',
                duration: 0,
            });

            // submit endpoint to API
            await submit(apolloClient, routerId, values).finally(() => {
                setLoading(false);
                message.destroy('primary');
            });

            // show success message
            message.success({
                content: t('routerDetails:addEndpoint.configuration.messages.success'),
                key: 'primary',
            });

            // then go back to router
            navigate(`/admin/system/routers/${routerId}`, { state: { defaultTab: 'endpoints' } });
        },
        [setLoading, t, submit, apolloClient, routerId, navigate]
    );

    const { FormComponent } = endpointClass;

    return (
        <Card>
            <Alert
                action={
                    <Button onClick={goBackToSelection} size="small" type="default">
                        {t('routerDetails:addEndpoint.configuration.cancelButton')}
                    </Button>
                }
                message={t('routerDetails:addEndpoint.configuration.infoMessage', { type: endpointClass.label })}
                type="info"
                showIcon
            />
            <br />
            <Formik initialValues={{}} onSubmit={onSubmit}>
                {({ handleSubmit }) => (
                    <Form layout="vertical" onSubmitCapture={handleSubmit}>
                        <FormComponent router={router} />
                        <div style={{ textAlign: 'right' }}>
                            <LoadingButton htmlType="submit" type="primary">
                                {t('routerDetails:addEndpoint.configuration.submitButton')}
                            </LoadingButton>
                        </div>
                    </Form>
                )}
            </Formik>
        </Card>
    );
};

export default EndpointClassConfiguration;
